import React, { useCallback, useContext, useEffect, useState } from 'react';

/**
 * Generic floating element registry. Allows child components to register
 * refs keyed by any value (number, string, etc.), which can then be
 * consumed by a floating element to calculate positions.
 *
 * Example: dealer chip registry (keyed by seat number), bounty bullet registry
 * (keyed by seat number), etc. Avoids duplicating the ref-tracking pattern.
 */

export type FloatingRegistryContextType<K> = {
  refs: Map<K, React.RefObject<HTMLDivElement>>;
  register: (key: K, ref: React.RefObject<HTMLDivElement>) => void;
  unregister: (key: K) => void;
};

/**
 * Factory that creates a typed Context + Provider pair for a floating element registry.
 * Each call produces an independent registry for a specific use case.
 *
 * @param displayName - Optional name for the context (used in React DevTools)
 * @returns Object with { Context, Provider }
 *
 * @example
 * const { Context: DealerChipContext, Provider: DealerChipProvider } = createFloatingRegistry<number>('DealerChip');
 * const { Context: BountyContext, Provider: BountyProvider } = createFloatingRegistry<number>('Bounty');
 */
export function createFloatingRegistry<K>(displayName?: string) {
  const Context = React.createContext<FloatingRegistryContextType<K>>({
    refs: new Map(),
    register: () => {},
    unregister: () => {}
  });

  Context.displayName = displayName ?? 'FloatingRegistry';

  const Provider = ({ children }: { children: React.ReactNode }) => {
    const [refs, setRefs] = useState<Map<K, React.RefObject<HTMLDivElement>>>(new Map());

    const register = useCallback((key: K, ref: React.RefObject<HTMLDivElement>) => {
      setRefs(prev => new Map(prev).set(key, ref));
    }, []);

    const unregister = useCallback((key: K) => {
      setRefs(prev => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }, []);

    return <Context.Provider value={{ refs, register, unregister }}>{children}</Context.Provider>;
  };

  return { Context, Provider };
}

/**
 * Computes the center of a target element relative to a root container,
 * returned as percentages. Used by floating elements (dealer button, bounty
 * bullet, etc.) to position themselves over target elements.
 *
 * @param ref - RefObject pointing to the target element (e.g. a dealer chip anchor)
 * @param rootSelector - Selector for the root container (default: '[data-table-root]')
 * @returns { leftPct, topPct } or null if measurement fails (element unmeasured, missing, etc.)
 *
 * @example
 * const { refs } = useContext(DealerChipContext);
 * const pos = resolveElementPosition(refs.get(seatNumber));
 * if (pos) {
 *   gsap.set(floatingElement, { left: `${pos.leftPct}%`, top: `${pos.topPct}%` });
 * }
 */
export function resolveElementPosition(
  ref: React.RefObject<HTMLDivElement> | undefined,
  rootSelector = '[data-table-root]'
): { leftPct: number; topPct: number } | null {
  if (!ref?.current) return null;

  const elementRect = ref.current.getBoundingClientRect();
  // The element's OWN table root — never `document.querySelector`, which
  // returns the first root in the whole document. Several table roots are
  // mounted at once (every grid cell, plus the kept-alive carousel panels),
  // so a document-wide lookup measured table 2's seats against table 1's
  // root and the dealer button flew through the gap between tables
  // (EN-10567). `closest` is the rule every other floating measurement in
  // the codebase already follows (WinChips, BetChips, PovCards, SeatCards).
  const rootElement = ref.current.closest(rootSelector);

  if (!rootElement) return null;

  const rootRect = rootElement.getBoundingClientRect();

  // Guard against unmeasured/hidden elements
  if (
    elementRect.width === 0 ||
    elementRect.height === 0 ||
    rootRect.width === 0 ||
    rootRect.height === 0
  ) {
    return null;
  }

  // Calculate center of the element relative to the root container
  const elementCenterLeft = elementRect.left + elementRect.width / 2 - rootRect.left;
  const elementCenterTop = elementRect.top + elementRect.height / 2 - rootRect.top;

  // Convert to percentages
  const leftPct = (elementCenterLeft / rootRect.width) * 100;
  const topPct = (elementCenterTop / rootRect.height) * 100;

  return { leftPct, topPct };
}

/**
 * Hook for child components to register their floating anchor refs with a registry.
 * Automatically cleans up on unmount.
 *
 * @param context - The FloatingRegistryContext (e.g. DealerChipContext)
 * @param key - The key this ref should be registered under (e.g. seatId)
 * @param ref - The RefObject to register
 *
 * @example
 * const dealerChipRef = useRef<HTMLDivElement>(null);
 * useRegisterFloatingAnchor(DealerChipContext, seatId, dealerChipRef);
 * return <div ref={dealerChipRef} style={...}>
 *   <DealerChip isPortrait={isPortrait} />
 * </div>
 */
export function useRegisterFloatingAnchor<K>(
  context: React.Context<FloatingRegistryContextType<K>>,
  key: K,
  ref: React.RefObject<HTMLDivElement>
): void {
  const { register, unregister } = useContext(context);

  useEffect(() => {
    register(key, ref);
    return () => unregister(key);
  }, [key, register, unregister]);
}
